import { FC } from 'react';

import { GiftDeliveryCategoryData } from '@shared/api/giftDeliveryCategory/giftDeliveryCategory.interface';
import { Link } from 'react-router-dom';

import { GiftItemInfoUsageLite } from '../utils/choiceUnitUsage.utils';

export type BrandDeliveryCategory = Pick<
  GiftDeliveryCategoryData,
  '_id' | 'name' | 'isEnabled' | 'order'
>;

/** ブランド와 카테고리의 연결은 원상품에 지정된 카테고리를 기준으로 집계한다. */
export const GiftBrandCategorySummary: FC<{
  brandName: string;
  products: GiftItemInfoUsageLite[] | null | undefined;
  categories: BrandDeliveryCategory[] | null | undefined;
  retrying: boolean;
  onRetry: () => void;
  onManage: (categoryId?: string) => void;
}> = ({ brandName, products, categories, retrying, onRetry, onManage }) => {
  const counts = new Map<string, number>();
  let unassignedCount = 0;
  products?.forEach((product) => {
    if (product.type !== 'Delivery') return;
    const category = product.giftDeliveryCategory;
    const id = typeof category === 'string' ? category : category?._id;
    if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
    else unassignedCount += 1;
  });
  const categoryById = new Map(categories?.map((category) => [category._id, category]));
  const connected = Array.from(counts, ([id, count]) => ({
    id,
    count,
    category: categoryById.get(id),
  })).sort(
    (a, b) =>
      (a.category?.order ?? Number.MAX_SAFE_INTEGER) -
        (b.category?.order ?? Number.MAX_SAFE_INTEGER) ||
      (a.category?.name ?? a.id).localeCompare(b.category?.name ?? b.id, 'ko'),
  );
  const failed = products === null || categories === null;
  const loading = products === undefined || categories === undefined;

  return (
    <div className="mt-3 border-t border-gray-100 pt-3 text-xs font-normal">
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-500">
        <span className="font-medium">배송카테고리</span>
        <span
          className="text-gray-400"
          title="원상품에 지정된 카테고리입니다. 판매상품별로 별도 지정한 카테고리는 포함하지 않습니다."
        >
          원상품 기준
        </span>
      </div>
      {failed ? (
        <div>
          <p className="text-gray-500">연결 정보를 불러오지 못했습니다.</p>
          <button
            type="button"
            aria-label="카테고리 연결 정보 다시 시도"
            onClick={onRetry}
            disabled={retrying}
            className="mt-1 rounded py-1 text-indigo-700 underline underline-offset-2 disabled:opacity-50"
          >
            {retrying ? '재조회 중…' : '다시 시도'}
          </button>
        </div>
      ) : loading ? (
        <p role="status" className="text-gray-400">
          연결 정보 확인 중…
        </p>
      ) : (
        <>
          <ul className="space-y-2" aria-label={`${brandName} 연결 배송카테고리`}>
            {connected.map(({ id, count, category }) => (
              <li key={id} className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                {category ? (
                  <Link
                    to={`/gift-seller/delivery-category/${encodeURIComponent(
                      id,
                    )}/edit?from=brand`}
                    aria-label={`${category.name} 카테고리 편집`}
                    title="카테고리 이름·이미지·하위분류 편집"
                    className="min-w-0 break-words rounded font-medium text-gray-700 underline decoration-gray-300 underline-offset-4 hover:text-indigo-700"
                  >
                    {category.name}
                    <span aria-hidden className="ml-1 text-gray-400">
                      ↗
                    </span>
                  </Link>
                ) : (
                  <span className="text-gray-500">카테고리 확인 필요</span>
                )}
                {category?.isEnabled === false && (
                  <span className="text-gray-400">미사용</span>
                )}
                <button
                  type="button"
                  onClick={() => onManage(category ? id : undefined)}
                  aria-label={`${
                    category?.name ?? '확인 필요 카테고리'
                  } 연결 상품 ${count}개 보기`}
                  className="shrink-0 rounded py-0.5 tabular-nums text-gray-500 hover:text-indigo-700 hover:underline"
                >
                  {count}개 상품
                </button>
              </li>
            ))}
          </ul>
          {connected.length === 0 && (
            <p className="text-gray-500">연결된 카테고리 없음</p>
          )}
          {unassignedCount > 0 && (
            <button
              type="button"
              aria-label={`${brandName} 카테고리 미지정 상품 ${unassignedCount}개 보기`}
              onClick={() => onManage('none')}
              className="mt-2 block rounded text-amber-700 hover:underline"
            >
              미지정 {unassignedCount}개 · 연결하기
            </button>
          )}
          <button
            type="button"
            aria-label={`${brandName} 상품 연결 관리`}
            onClick={() => onManage()}
            className="mt-2 rounded py-1 text-gray-600 underline underline-offset-4 hover:text-indigo-700"
          >
            상품 연결 관리
          </button>
        </>
      )}
    </div>
  );
};
